import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type RefObject,
} from "react";
import {
  getDefaultLocationDisplayMode,
  type LocationDisplayMode,
} from "../../../helpers/locationDisplayUtils";

interface LocationDisplayModeContextValue {
  activeMode: LocationDisplayMode;
  setActiveMode: (mode: LocationDisplayMode) => void;
  tierCount: number;
  containerRef: RefObject<HTMLDivElement>;
}

const LocationDisplayModeContext =
  createContext<LocationDisplayModeContextValue | null>(null);

interface LocationDisplayModeProviderProps {
  tierCount: number;
  containerRef: RefObject<HTMLDivElement>;
  children: ReactNode;
}

export const LocationDisplayModeProvider = ({
  tierCount,
  containerRef,
  children,
}: LocationDisplayModeProviderProps) => {
  const safeTierCount = tierCount <= 0 ? 1 : tierCount;
  const [activeMode, setActiveMode] = useState<LocationDisplayMode>(() =>
    getDefaultLocationDisplayMode(safeTierCount),
  );

  useEffect(() => {
    setActiveMode(getDefaultLocationDisplayMode(safeTierCount));
  }, [safeTierCount]);

  const value = useMemo(
    () => ({
      activeMode,
      setActiveMode,
      tierCount: safeTierCount,
      containerRef,
    }),
    [activeMode, safeTierCount, containerRef],
  );

  return (
    <LocationDisplayModeContext.Provider value={value}>
      {children}
    </LocationDisplayModeContext.Provider>
  );
};

export function useLocationDisplayMode(): LocationDisplayModeContextValue {
  const context = useContext(LocationDisplayModeContext);
  if (!context) {
    throw new Error(
      "useLocationDisplayMode must be used within LocationDisplayModeProvider",
    );
  }
  return context;
}

export function locationDisplayModesEqual(
  a: LocationDisplayMode,
  b: LocationDisplayMode,
): boolean {
  if (a.kind !== b.kind) {
    return false;
  }
  if (a.kind === "fullPath") {
    return true;
  }
  if (b.kind === "tier") {
    return a.tierIndex === b.tierIndex;
  }
  return false;
}
